import React, { useState } from 'react';
import { Button } from 'components/ui';
import ResponsiveInspirationPopover from 'components/shared/ResponsiveInspirationPopover';
import { HiMagicWand, HiSparkles, HiLightBulb } from 'react-icons/hi2';
import { BsStars, BsLightning } from 'react-icons/bs';
import useRandomContexts from 'utils/hooks/useRandomContexts';
import { BiSolidMagicWand } from 'react-icons/bi';
import { TbLoader2 } from 'react-icons/tb';

const AIImageInspirationDropdown = ({ setQuery, className = "" }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [customTopic, setCustomTopic] = useState('');
    const [isLoadingSmart, setIsLoadingSmart] = useState(false);
    const [isLoadingCustom, setIsLoadingCustom] = useState(false);
    const { generateRandomAIImagePrompts } = useRandomContexts();

    const handleSmartSuggestion = async () => {
        setIsLoadingSmart(true);
        try {
            const suggestions = await generateRandomAIImagePrompts(1, true);
            if (suggestions?.length > 0) {
                setQuery(suggestions[0]);
                setIsOpen(false);
            }
        } catch (error) {
            console.error('Error generating smart suggestion:', error);
        } finally {
            setIsLoadingSmart(false);
        }
    };

    const handleCustomSuggestion = async () => {
        if (!customTopic.trim()) return;
        
        setIsLoadingCustom(true);
        try {
            const suggestions = await generateRandomAIImagePrompts(1, true, customTopic.trim());
            if (suggestions?.length > 0) {
                setQuery(suggestions[0]);
                setIsOpen(false);
                setCustomTopic('');
            }
        } catch (error) {
            console.error('Error generating custom suggestion:', error);
        } finally {
            setIsLoadingCustom(false);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleCustomSuggestion();
        }
    };

    const trigger = (
        <Button
            variant="plain"
            size="sm"
            className={`!p-2 !w-auto !min-w-none !h-auto !rounded-lg !bg-white/60 dark:!bg-gray-800/80 !backdrop-blur-sm hover:!bg-white/80 dark:hover:!bg-gray-800/80 !transition-all !duration-300 !shadow hover:!shadow-md group ${className}`}
            icon={
                isLoadingSmart || isLoadingCustom 
                ? <TbLoader2 className="w-4 h-4 text-[13px] text-pink-600 dark:text-pink-400 group-hover:text-pink-700 dark:group-hover:text-pink-300 transition-colors animate-spin" /> 
                : <BiSolidMagicWand className="w-4 h-4 text-[13px] text-pink-600 dark:text-pink-400 group-hover:text-pink-700 dark:group-hover:text-pink-300 transition-colors" />}
        />
    );

    const content = (
        <div className="space-y-4">
            {/* Header */}
            <div className="flex items-center gap-2 pb-2 border-b border-gray-100 dark:border-gray-700">
                <div className="w-8 h-8 bg-gradient-to-br from-pink-500 to-orange-600 rounded-lg flex items-center justify-center">
                    <HiSparkles className="w-4 h-4 text-white" />
                </div>
                <div>
                    <div className="font-semibold text-gray-900 dark:text-gray-100">
                        Image Inspiration
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                        Get creative ideas for your image
                    </div>
                </div>
            </div>

            {/* Smart Suggestion Option */}
            <div className="space-y-2">
                <div className="flex items-center gap-2">
                    <BsStars className="w-4 h-4 text-pink-500" />
                    <span className="font-medium text-gray-900 dark:text-gray-100">Smart Suggestion</span>
                </div>
                <p className="text-xs text-gray-600 dark:text-gray-400 mb-3">
                    Get a random creative image idea tailored to your brand
                </p>
                <Button
                    variant="solid"
                    size="sm"
                    loading={isLoadingSmart}
                    onClick={handleSmartSuggestion}
                    className="w-full !bg-gradient-to-r !from-pink-500 !to-orange-600 hover:!from-pink-600 hover:!to-orange-700 !text-white !font-medium"
                    icon={<BsLightning className="w-4 h-4" />}
                >
                    Generate Smart Idea
                </Button>
            </div>

            {/* Divider */}
            <div className="relative">
                <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-200 dark:border-gray-600" />
                </div>
                <div className="relative flex justify-center text-xs">
                    <span className="bg-white dark:bg-gray-800 px-2 text-gray-500 dark:text-gray-400">or</span>
                </div>
            </div>

            {/* Custom Topic Option */}
            <div className="space-y-2">
                <div className="flex items-center gap-2">
                    <HiLightBulb className="w-4 h-4 text-amber-500" />
                    <span className="font-medium text-gray-900 dark:text-gray-100">Custom Topic</span>
                </div>
                <p className="text-xs text-gray-600 dark:text-gray-400 mb-3">
                    Describe a topic or theme and we'll create an image idea
                </p>
                <div className="space-y-3">
                    <textarea
                        value={customTopic}
                        onChange={(e) => setCustomTopic(e.target.value)}
                        onKeyPress={handleKeyPress}
                        placeholder="e.g., summer vacation, healthy eating, productivity tips..."
                        className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent resize-none transition-all duration-200"
                        rows={3}
                    />
                    <Button
                        variant="solid"
                        size="sm"
                        loading={isLoadingCustom}
                        onClick={handleCustomSuggestion}
                        disabled={!customTopic.trim()}
                        className="w-full !bg-gradient-to-r !from-amber-500 !to-orange-600 hover:!from-amber-600 hover:!to-orange-700 !text-white !font-medium disabled:!opacity-50 disabled:!cursor-not-allowed"
                        icon={<HiSparkles className="w-4 h-4" />}
                    >
                        Generate Custom Idea
                    </Button>
                </div>
            </div>
        </div>
    );

    return (
        <ResponsiveInspirationPopover
            isOpen={isOpen}
            onClose={() => setIsOpen(false)}
            positions={['right', 'center']}
            title="Image Inspiration"
            content={content}
        >
            <div onClick={() => setIsOpen(!isOpen)}>
                {trigger}
            </div>
        </ResponsiveInspirationPopover>
    );
};

export default AIImageInspirationDropdown;
